/**
 * Centralized SignalR Service
 * Singleton pattern to manage SignalR connection across all Vue components
 */
import * as signalR from "@microsoft/signalr";
import axios from "axios";

class SignalRService {
  constructor() {
    this.connection = null;
    this.token = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.connectionPromise = null;
    this.eventHandlers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.urlApi = import.meta.env.VITE_APP_API || 'http://localhost:5000/';
    // Debug mode - set to true to enable logging
    this.debug = import.meta.env.VITE_SIGNALR_DEBUG === 'true' || false;
  }

  /**
   * Log helper - only logs when debug mode is enabled
   * @private
   */
  log(...args) {
    if (this.debug) {
      console.log('[SignalR Service]', ...args);
    }
  }

  /**
   * Error log helper - always logs errors
   * @private
   */
  logError(...args) {
    console.error('[SignalR Service]', ...args);
  }

  /**
   * Get SignalR token from LoginLHB API
   * @param {string} username 
   * @param {string} roleId 
   * @returns {Promise<string>}
   */
  async getToken(username, roleId) {
    // If we already have a token, return it
    if (this.token) {
      this.log('Using cached token');
      return this.token;
    }

    this.log('Fetching new token for username:', username, 'roleId:', roleId);

    try {
      const response = await axios.post(`${this.urlApi}api/v1/LHB/GetTokenChatLHBAdmin`, {
        username: username,
        roleId: roleId?.toString() || '0'
      }, {
        timeout: 15000 // 15 second timeout
      });

      if (response.data?.status === 0 && response.data?.data?.token) {
        this.token = response.data.data.token;
        this.log('Token obtained successfully');
        return this.token;
      } else {
        this.logError('Failed to get SignalR token:', response.data);
        return '';
      }
    } catch (error) {
      this.logError('LoginLHB Error:', error);
      return '';
    }
  }

  /**
   * Initialize and connect to SignalR hub
   * @param {string} username 
   * @param {string} roleId 
   * @returns {Promise<signalR.HubConnection>}
   */
  async connect(username, roleId) {
    // If already connected, return existing connection
    if (this.connection && this.isConnected) {
      this.log('Already connected, reusing connection');
      return this.connection;
    }

    // If connection is in progress, wait for it
    if (this.isConnecting && this.connectionPromise) {
      this.log('Connection in progress, waiting...');
      return this.connectionPromise;
    }

    this.isConnecting = true;
    
    this.connectionPromise = this._createConnection(username, roleId);
    
    try {
      const connection = await this.connectionPromise;
      this.isConnecting = false;
      return connection;
    } catch (error) {
      this.isConnecting = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  /**
   * Internal method to create SignalR connection
   * @private
   */
  async _createConnection(username, roleId) {
    this.log('Starting connection creation...');
    
    // Get token first
    await this.getToken(username, roleId);
    this.log('Token obtained:', this.token ? 'YES' : 'NO');

    const self = this;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.urlApi}hub`, {
        transport: signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
        accessTokenFactory: () => {
          return self.token;
        }
      })
      .withAutomaticReconnect([5000, 5000, 10000, 10000, 15000])
      .build();

    this.connection.serverTimeoutInMilliseconds = 120000; // 2 minutes

    // Handle connection lifecycle events
    this.connection.onclose((error) => {
      self.log('Connection closed', error);
      self.isConnected = false;
      self._notifyHandlers('connectionClosed', error);
    });

    this.connection.onreconnecting((error) => {
      self.log('Reconnecting...', error);
      self._notifyHandlers('reconnecting', error);
    });

    this.connection.onreconnected((connectionId) => {
      self.log('Reconnected. Connection ID:', connectionId);
      self.isConnected = true;
      self.reconnectAttempts = 0;
      self._notifyHandlers('reconnected', connectionId);
    });

    try {
      await this.connection.start();
      this.log('Connected! Connection ID:', this.connection.connectionId);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      return this.connection;
    } catch (err) {
      this.logError('Connection Error:', err);
      this.isConnected = false;
      throw err;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
        this.log('Disconnected!');
      } catch (error) {
        this.logError('Error stopping connection:', error);
      }
      this.isConnected = false;
      this.connection = null;
      this.token = null;
      this.connectionPromise = null;
    }
  }

  /**
   * Register an event handler for SignalR events
   * @param {string} eventName - The name of the SignalR event
   * @param {string} handlerId - Unique identifier for the handler (e.g., component name)
   * @param {Function} callback - The callback function
   */
  on(eventName, handlerId, callback) {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Map());
      
      // Register the event handler on the connection if connected
      if (this.connection) {
        this.connection.on(eventName, (...args) => {
          this._invokeHandlers(eventName, ...args);
        });
      }
    }
    
    this.eventHandlers.get(eventName).set(handlerId, callback);
  }

  /**
   * Unregister an event handler
   * @param {string} eventName - The name of the SignalR event
   * @param {string} handlerId - Unique identifier for the handler
   */
  off(eventName, handlerId) {
    if (this.eventHandlers.has(eventName)) {
      this.eventHandlers.get(eventName).delete(handlerId);
    }
  }

  /**
   * Unregister all event handlers for a specific handler ID (e.g., when component unmounts)
   * @param {string} handlerId - Unique identifier for the handler
   */
  offAll(handlerId) {
    for (const [eventName, handlers] of this.eventHandlers) {
      handlers.delete(handlerId);
    }
  }

  /**
   * Invoke a method on the SignalR hub
   * @param {string} methodName - The method to invoke
   * @param  {...any} args - Arguments to pass
   */
  async invoke(methodName, ...args) {
    if (!this.connection || !this.isConnected) {
      throw new Error('SignalR not connected');
    }
    return await this.connection.invoke(methodName, ...args);
  }

  /**
   * Get connection state
   * @returns {string}
   */
  getState() {
    if (!this.connection) {
      return 'Disconnected';
    }
    return signalR.HubConnectionState[this.connection.state];
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  get connected() {
    return this.connection && 
           this.connection.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get connection ID
   * @returns {string|null}
   */
  get connectionId() {
    return this.connection?.connectionId || null;
  }

  /**
   * Internal method to invoke all registered handlers for an event
   * @private
   */
  _invokeHandlers(eventName, ...args) {
    if (this.eventHandlers.has(eventName)) {
      for (const [handlerId, callback] of this.eventHandlers.get(eventName)) {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in SignalR event handler [${eventName}][${handlerId}]:`, error);
        }
      }
    }
  }

  /**
   * Internal method to notify connection lifecycle handlers
   * @private
   */
  _notifyHandlers(eventType, data) {
    if (this.eventHandlers.has(`_${eventType}`)) {
      for (const [, callback] of this.eventHandlers.get(`_${eventType}`)) {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in lifecycle handler [${eventType}]:`, error);
        }
      }
    }
  }

  /**
   * Register event handlers on the connection after it's established
   * Called internally when connection is created
   */
  _registerPendingHandlers() {
    if (!this.connection) return;
    
    for (const [eventName] of this.eventHandlers) {
      if (!eventName.startsWith('_')) {
        this.connection.on(eventName, (...args) => {
          this._invokeHandlers(eventName, ...args);
        });
      }
    }
  }
}

// Export singleton instance
const signalRService = new SignalRService();
export default signalRService;
